import { PromiseResolvers } from '@nutrien/minesight-utility-module';

import { KindOf } from '@/utilities/HasKind';

import { Goals } from './actions/goals/Goals';
import { Crumbs } from './bakery/Crumbs';

// While the replication lifecycle evolves,
// various asynchronous callers are waiting for results.
export type GoalResolvers = {
  [K in KindOf<Goals>]: PromiseResolvers<void>[];
};

export function addGoal(goalResolvers: GoalResolvers, goal: Goals): GoalResolvers {
  const forGoal = goalResolvers[goal.kind];
  const newForGoal = [...forGoal, goal.resolvers];
  return {
    ...goalResolvers,
    [goal.kind]: newForGoal,
  };
}

export function createGoalResolvers(initiator?: Goals): GoalResolvers {
  let goalResolvers: GoalResolvers = {
    Destroy: [],
    Disable: [],
    LogOut: [],
    Restart: [],
    Rebuild: [],
    Start: [],
    Stop: [],
  };
  if (initiator) {
    goalResolvers = addGoal(goalResolvers, initiator);
  }
  return goalResolvers;
}

export function crumble(goalResolvers: GoalResolvers): Crumbs {
  return Object.entries(goalResolvers).reduce((state, [kind, resolvers]) => {
    // We instaniated `state` here with the express purpose of mutating it.
    // eslint-disable-next-line no-param-reassign
    state[kind] = resolvers.length;
    return state;
  }, {} as Record<string, number>);
}

export function resolve(goalResolvers: GoalResolvers, kind: KindOf<Goals>): GoalResolvers {
  goalResolvers[kind].forEach(r => r.resolve());

  const newResolvers = {
    ...goalResolvers,
    [kind]: [],
  };
  return newResolvers;
}

export function reject(
  goalResolvers: GoalResolvers,
  kind: KindOf<Goals>,
  reason: unknown,
): GoalResolvers {
  goalResolvers[kind].forEach(r => {
    // Rejecting should be rare if we have the life cycle working correctly.
    // Not every goal is logging its promise rejections (as a lot of them are not awaited at all)
    // so we'll log them here to assist with tracking down problems.
    // Note that we don't also need this in `Sentry`;
    // if it's causing a problem, the problem itself will be logged there.
    // eslint-disable-next-line no-console
    console.debug('Rejecting Goal', kind, reason);
    r.reject(reason);
  });

  const newResolvers = {
    ...goalResolvers,
    [kind]: [],
  };
  return newResolvers;
}

export function rejectAll(resolvers: GoalResolvers, reason: unknown) {
  return Object.keys(resolvers).reduce((state, kind) => {
    return reject(state, kind as keyof GoalResolvers, reason);
  }, resolvers);
}
